import React from 'react'
import { Box, Dialog, styled } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/design-system'
import { FEATURE_KEYS, FEATURES_MAP, FeatureKey } from '@/lib/featureKeys'

// Night paywall shell — studio-dark chrome, not the light design-system
// Modal. Studio chrome + light modal was the mismatch; icon sat under an
// empty Modal header row.

const Paper = styled(Box)({
  width: 480,
  maxWidth: 'calc(100vw - 32px)',
  borderRadius: 20,
  padding: '30px 32px 26px',
  background: 'rgba(23,22,29,0.98)',
  border: '1px solid rgba(244,243,246,0.12)',
  boxShadow: '0 28px 80px rgba(0,0,0,0.55)',
  color: 'var(--ds-night-ink)',
  textAlign: 'center',
})

const GradientIcon = styled('div')({
  width: 54,
  height: 54,
  margin: '0 auto 16px',
  borderRadius: 16,
  background: 'var(--ds-gradient-spectrum, linear-gradient(135deg, #ffcf7d, #fea67d, #be9dfd, #01dafe))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 24,
  lineHeight: 1,
  flexShrink: 0,
})

const Headline = styled('h2')({
  margin: '0 0 8px',
  fontSize: 19,
  fontWeight: 720,
  letterSpacing: '-0.02em',
  color: 'var(--ds-night-ink)',
})

const Pitch = styled('p')({
  margin: '0 0 22px',
  fontSize: 13,
  lineHeight: 1.6,
  color: 'var(--ds-night-ink-muted)',
})

const PlansRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 10,
  marginBottom: 20,
  textAlign: 'left',
})

const PlanCard = styled(Box, {
  shouldForwardProp: (p) => p !== '$highlight',
})<{ $highlight?: boolean }>(({ $highlight }) => ({
  padding: '14px 15px',
  borderRadius: 13,
  border: `1.5px solid ${$highlight ? 'rgba(190,157,253,0.55)' : 'var(--ds-night-border)'}`,
  background: $highlight ? 'rgba(190,157,253,0.08)' : 'transparent',
  position: 'relative',
}))

const PlanBadge = styled('span')({
  position: 'absolute',
  top: -9,
  left: 12,
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 7.5,
  fontWeight: 700,
  letterSpacing: '0.08em',
  padding: '2px 7px',
  borderRadius: 999,
  background: 'var(--ds-gradient-spectrum)',
  color: '#17130a',
})

const PlanName = styled('div')({
  fontSize: 13.5,
  fontWeight: 700,
  color: 'var(--ds-night-ink)',
})
const PlanPrice = styled('div')({
  fontSize: 11.5,
  color: 'var(--ds-night-ink-subtle)',
  margin: '1px 0 9px',
})

const FeatureList = styled('ul')({
  margin: 0,
  padding: 0,
  listStyle: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
})

const FeatureItem = styled('li', {
  shouldForwardProp: (p) => p !== '$dim',
})<{ $dim?: boolean }>(({ $dim }) => ({
  fontSize: 11,
  color: 'var(--ds-night-ink-muted)',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 6,
  opacity: $dim ? 0.45 : 1,
  '&::before': {
    content: $dim ? '"–"' : '"✓"',
    fontWeight: 700,
    flexShrink: 0,
    color: $dim ? 'var(--ds-night-ink-subtle)' : 'var(--ds-accent-dot-3, #be9dfd)',
    fontSize: 10,
    marginTop: 1,
  },
}))

const Footer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  paddingTop: 16,
  borderTop: '1px solid var(--ds-night-border)',
})

const Legal = styled('p')({
  margin: 0,
  fontSize: 10.5,
  color: 'var(--ds-night-ink-subtle)',
  lineHeight: 1.5,
  textAlign: 'left',
})

const GhostBtn = styled(Button)({
  color: 'var(--ds-night-ink-muted) !important',
  background: 'transparent !important',
  '&:hover': {
    color: 'var(--ds-night-ink) !important',
    background: 'transparent !important',
  },
})

const GoBtn = styled(Button)({
  background: 'var(--ds-night-ink) !important',
  color: '#17130a !important',
  '&:hover': { opacity: 0.88 },
})

// ─── Plan definitions ─────────────────────────────────────────────────────────

type PlanId = 'Gallery' | 'Studio'

const PLANS: Record<PlanId, { name: string; price: string; features: string[]; dimmed: string[] }> = {
  Gallery: {
    name: 'Gallery',
    price: '$9 / month',
    features: [
      'Unlimited albums',
      '3D walkable galleries (14 Classic + Studio)',
      'Unlimited share keys + QR',
      'Reactions, comments & pins',
    ],
    dimmed: ['Guest uploads (soon)', 'Voice notes (soon)', 'Client workspaces'],
  },
  Studio: {
    name: 'Studio',
    price: '$24 / month',
    features: ['Everything in Gallery', 'Priority support'],
    dimmed: ['Client workspaces (soon)', 'RAW proofing (soon)', 'Analytics (soon)'],
  },
}

/** Multi-room gate copy — mirrors the standard paywall cards above. */
const MULTI_ROOM_PLANS: Record<PlanId, { features: string[]; dimmed: string[] }> = {
  Gallery: {
    features: [
      'Up to 3 connected rooms',
      '3D walkable galleries',
      'Guest uploads',
      'Unlimited share keys + QR',
    ],
    dimmed: [],
  },
  Studio: {
    features: [
      'Unlimited connected rooms',
      'Client workspaces & branding',
      'RAW proofing',
      'Gallery analytics',
    ],
    dimmed: [],
  },
}

const ICONS: Record<string, string> = {
  gallery_3d: '◈',
  studio_multi_room: '◈',
  multi_source_albums: '🖼',
  studio_custom_finishes: '🎨',
  unlimited_albums: '📚',
  guest_uploads: '📤',
  voice_notes: '🎙',
  anniversary_reopening: '🎉',
  client_workspaces: '🏢',
  raw_proofing: '📷',
  analytics: '📊',
  studio_print_quota: '🖼',
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface UpgradeModalProps {
  open: boolean
  featureKey: FeatureKey | null
  headline?: string
  /** Override which plan is recommended (e.g. Studio when Gallery room cap is hit). */
  upgradeTo?: PlanId
  onClose: () => void
}

export function UpgradeModal({ open, featureKey, headline, upgradeTo, onClose }: UpgradeModalProps) {
  const navigate = useNavigate()
  const def = featureKey ? FEATURES_MAP[featureKey] : null
  const targetPlan: PlanId = upgradeTo ?? def?.upgradeTo ?? 'Gallery'
  const otherId: PlanId = targetPlan === 'Gallery' ? 'Studio' : 'Gallery'
  const multiRoom = featureKey === FEATURE_KEYS.STUDIO_MULTI_ROOM

  const resolvePlan = (id: PlanId) =>
    multiRoom ? { ...PLANS[id], ...MULTI_ROOM_PLANS[id] } : PLANS[id]

  const plan = resolvePlan(targetPlan)
  const otherPlan = resolvePlan(otherId)

  const icon = featureKey ? (ICONS[featureKey] ?? '✦') : '✦'
  const title = headline ?? (def ? `Unlock ${def.label}` : 'Upgrade your plan')
  const pitch = def?.pitch ?? 'This feature requires a paid plan.'

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          background: 'transparent',
          boxShadow: 'none',
          margin: 2,
          maxWidth: 520,
          width: '100%',
          overflow: 'visible',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(6,5,10,0.6)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
          },
        },
      }}
    >
      <Paper>
        <GradientIcon aria-hidden>{icon}</GradientIcon>
        <Headline>{title}</Headline>
        <Pitch>{pitch}</Pitch>

        <PlansRow>
          <PlanCard $highlight>
            <PlanBadge>RECOMMENDED</PlanBadge>
            <PlanName>{plan.name}</PlanName>
            <PlanPrice>{plan.price}</PlanPrice>
            <FeatureList>
              {plan.features.map((f) => (
                <FeatureItem key={f}>{f}</FeatureItem>
              ))}
              {plan.dimmed.map((f) => (
                <FeatureItem key={f} $dim>
                  {f}
                </FeatureItem>
              ))}
            </FeatureList>
          </PlanCard>

          <PlanCard>
            <PlanName>{otherPlan.name}</PlanName>
            <PlanPrice>{otherPlan.price}</PlanPrice>
            <FeatureList>
              {otherPlan.features.map((f) => (
                <FeatureItem key={f}>{f}</FeatureItem>
              ))}
              {otherPlan.dimmed.map((f) => (
                <FeatureItem key={f} $dim>
                  {f}
                </FeatureItem>
              ))}
            </FeatureList>
          </PlanCard>
        </PlansRow>

        <Footer>
          <Legal>
            Billed monthly or yearly.
            <br />
            Cancel anytime.
          </Legal>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <GhostBtn variant='ghost' size='sm' onClick={onClose}>
              Maybe later
            </GhostBtn>
            <GoBtn
              variant='primary'
              size='sm'
              onClick={() => {
                onClose()
                navigate('/pricing')
              }}
            >
              See plans
            </GoBtn>
          </Box>
        </Footer>
      </Paper>
    </Dialog>
  )
}
